import React, { useState } from 'react';
import { ExternalLink, FileText, Receipt } from 'lucide-react';
import { isPdfReceipt } from '../../lib/expenseReceiptDisplay';
import { useImageViewer } from '../ImageViewerProvider';

export interface ExpenseReceiptPreviewProps {
  url: string;
  mimeType?: string | null;
  fileName?: string | null;
  /** Compact thumbnail for list rows */
  variant?: 'thumbnail' | 'full';
  className?: string;
  imgClassName?: string;
  /** Show open-in-new-tab control (full view) */
  showOpenLink?: boolean;
}

export default function ExpenseReceiptPreview({
  url,
  mimeType,
  fileName,
  variant = 'full',
  className = '',
  imgClassName = '',
  showOpenLink = true,
}: ExpenseReceiptPreviewProps) {
  const isPdf = isPdfReceipt({ url, mimeType, fileName });
  const [imageFailed, setImageFailed] = useState(false);
  const displayName = fileName || 'Receipt.pdf';
  const showAsPdf = isPdf || imageFailed;
  const { open: openImageViewer } = useImageViewer();
  const openReceiptInViewer = () =>
    openImageViewer({ images: [{ src: url, caption: fileName || 'Receipt', download: fileName || undefined }], index: 0 });

  if (variant === 'thumbnail') {
    if (showAsPdf) {
      return (
        <div
          className={`flex flex-col items-center justify-center bg-red-50 border border-red-100 rounded ${className}`}
          title={displayName}
        >
          <FileText className="h-5 w-5 text-red-500" aria-hidden />
          <span className="mt-0.5 text-[8px] font-semibold uppercase tracking-wide text-red-600">
            PDF
          </span>
        </div>
      );
    }

    return (
      <img
        src={url}
        alt="Receipt"
        className={`object-cover rounded border border-gray-200 ${imgClassName || className}`}
        onError={() => setImageFailed(true)}
      />
    );
  }

  // Full preview (modal)
  return (
    <div className={`relative rounded-2xl overflow-hidden bg-gray-100 border border-gray-200 ${className}`}>
      {showAsPdf ? (
        <div className="flex flex-col min-h-[280px] sm:min-h-[360px]">
          <div className="flex items-center justify-between gap-2 px-4 py-2.5 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center gap-2 min-w-0">
              <FileText className="h-4 w-4 shrink-0 text-red-500" aria-hidden />
              <span className="text-sm font-medium text-gray-900 truncate">{displayName}</span>
            </div>
            {showOpenLink ? (
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 shrink-0 text-xs font-medium text-blue-600 hover:text-blue-700"
              >
                Open
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            ) : null}
          </div>
          <iframe
            src={`${url}#toolbar=1&navpanes=0`}
            title={displayName}
            className="w-full flex-1 border-0 bg-white"
            style={{ minHeight: '320px', height: 'min(60vh, 480px)' }}
          />
        </div>
      ) : (
        <img
          src={url}
          alt="Receipt"
          className={`w-full h-auto cursor-zoom-in ${imgClassName}`}
          onClick={openReceiptInViewer}
          onError={() => setImageFailed(true)}
        />
      )}
      {showOpenLink && !showAsPdf ? (
        <button
          type="button"
          onClick={openReceiptInViewer}
          className="absolute top-3 right-3 p-2 bg-white/90 hover:bg-white rounded-xl shadow-lg transition-colors backdrop-blur-sm"
          aria-label="Open receipt"
        >
          <Receipt className="h-4 w-4 text-gray-600" />
        </button>
      ) : null}
      {showOpenLink && showAsPdf ? (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="absolute bottom-3 right-3 flex items-center gap-1.5 rounded-xl bg-white/95 px-3 py-2 text-xs font-medium text-gray-700 shadow-lg hover:bg-white"
        >
          <ExternalLink className="h-3.5 w-3.5" />
          Open PDF
        </a>
      ) : null}
    </div>
  );
}
